import { useEffect, useRef } from 'react';
import { notifyAiLimit } from '@/shared/utils/apiError';
import { routeScanEvent, type ScanEventCallbacks } from './scanEventRouter';
import { openOnboardingStream, replayRest } from '../replay';
import { SCAN_BASE } from '@utils/apiUtilsConstants';
import type { NameSource } from '../utils/siteIdentity';

const RECONNECT_TIMEOUT_MS = 20_000;
const INACTIVITY_TIMEOUT_MS = 120_000;
// The scan sends a heartbeat at least every few seconds while it works, so two
// minutes of silence means the connection died, not the scan. Reopen it from the
// last event a couple of times before telling the user anything is wrong.
const MAX_STREAM_REOPENS = 2;
// analysis-ready lands before the content strategist has produced a single
// persona. Rather than end the run there, hold the stream open until the screen
// says it has shown what it was waiting for. This bounds the wait for the NEXT
// piece rather than the whole hold: the last persona of a run has been seen
// arriving 106 seconds after the one before it, and a hold measured from
// analysis-ready closed the stream before it was ever sent.
const HANDOFF_HOLD_MAX_MS = 150_000;
const HOLD_POLL_MS = 500;

export type ScanFailReason = 'stalled' | 'lost' | 'duplicate' | 'generic';

export interface ScanConnectionOptions extends ScanEventCallbacks {
  handOffOnAnalysisReady?: boolean;
  /** Read through refs: the effect captures it once and polls it while holding. */
  readyToHandOff?: () => boolean;
  /**
   * A count that rises whenever the run delivers another piece of what the hold
   * is waiting for. While it keeps rising the hold keeps waiting, so a slow run
   * is not cut off mid-delivery.
   */
  holdProgress?: () => number;
  url: string;
  name: string;
  nameSource?: NameSource;
  businessType?: string;
  initialScanId?: string | null;
  initialPct?: number;
  initialProgressFloor?: number;
  initialProgressCeiling?: number;
  resumeKey?: number;
  onPctChange: (pct: number) => void;
  /** Running count of findings on the stream, for the signals odometer. */
  onSignalsChange?: (total: number) => void;
  onScanCreated?: (id: string) => void;
  onCompanyCreated?: (companyId: number) => void;
  onDone: () => void;
  onError?: (reason: ScanFailReason, detail?: ScanFailDetail) => void;
}

export interface ScanFailDetail {
  existingCompany?: string;
  existingCompanyId?: number;
}

interface ScanCreateResult {
  scanId: string;
  dbId?: number;
  companyId?: number;
}

export async function requestScanResume(scanId: string): Promise<void> {
  const res = await fetch(`${SCAN_BASE}/${scanId}/resume`, { method: 'POST', credentials: 'include' });
  if (!res.ok) throw new Error(`Resume failed: ${res.status}`);
}

export function useScanConnection(opts: ScanConnectionOptions) {
  const pctRef = useRef(Math.round(opts.initialPct ?? 0));
  const exactPctRef = useRef(opts.initialPct ?? 0);
  const doneRef = useRef(false);
  const failOnce = useRef(false);
  const scanIdRef = useRef<string | null>(opts.initialScanId ?? null);
  const dbIdRef = useRef<number | null>(null);
  const boundsRef = useRef<{ floor: number; ceiling: number }>({ floor: 0, ceiling: 90 });
  const signalsRef = useRef(0);
  const holdingRef = useRef(false);
  const holdPollRef = useRef<number | null>(null);

  const applyPct = (value: number) => {
    exactPctRef.current = Math.max(exactPctRef.current, Math.min(value, 90));
    const rounded = Math.round(exactPctRef.current);
    if (rounded === pctRef.current) return;
    pctRef.current = rounded;
    opts.onPctChange(rounded);
  };

  const settlePct = (value: number) => {
    exactPctRef.current = value;
    pctRef.current = value;
    opts.onPctChange(value);
  };

  const bump = (amount: number) => {
    applyPct(Math.min(exactPctRef.current + amount, boundsRef.current.ceiling));
  };

  const setProgressBounds = (floor: number, ceiling: number) => {
    boundsRef.current = { floor, ceiling: Math.max(floor, ceiling) };
    applyPct(floor);
  };

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    failOnce.current = false;
    doneRef.current = false;
    signalsRef.current = 0;
    setProgressBounds(opts.initialProgressFloor ?? 0, opts.initialProgressCeiling ?? 90);

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
    let reopensLeft = MAX_STREAM_REOPENS;
    let lastEventId = '';

    const clearReconnectTimer = () => { if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; } };
    const clearInactivityTimer = () => { if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; } };
    const clearHold = () => {
      holdingRef.current = false;
      if (holdPollRef.current !== null) { clearInterval(holdPollRef.current); holdPollRef.current = null; }
    };
    const resetInactivityTimer = () => {
      clearInactivityTimer();
      inactivityTimer = setTimeout(() => {
        if (!cancelled && !doneRef.current) reopenOrFail('stalled');
      }, INACTIVITY_TIMEOUT_MS);
    };

    const reopenOrFail = (reason: ScanFailReason) => {
      clearReconnectTimer();
      clearInactivityTimer();
      es?.close();
      const scanId = scanIdRef.current;
      if (scanId && reopensLeft > 0) {
        reopensLeft -= 1;
        connectStream(scanId);
        return;
      }
      triggerFail(reason);
    };

    const triggerFail = (reason: ScanFailReason = 'generic', detail?: ScanFailDetail) => {
      if (failOnce.current) return;
      failOnce.current = true;
      opts.onError?.(reason, detail);
    };

    const callbacks: ScanEventCallbacks = {
      onScanComplete: opts.onScanComplete,
      onAnalysisReady: opts.onAnalysisReady,
      onPartialResult: opts.onPartialResult,
      onBrandNameDetected: opts.onBrandNameDetected,
      onAgentStep: opts.onAgentStep,
      onAgentEvent: opts.onAgentEvent,
      onAgentLifecycle: opts.onAgentLifecycle,
      onSlowStep: opts.onSlowStep,
      onSiteScanStep: opts.onSiteScanStep,
      onAudienceFact: opts.onAudienceFact,
    };

    const finishRun = () => {
      if (doneRef.current) return;
      clearHold();
      clearInactivityTimer();
      settlePct(100);
      es?.close();
      doneRef.current = true;
      setTimeout(() => { if (!cancelled) opts.onDone(); }, 700);
    };

    const handleMessage = (rawData: string) => {
      const result = routeScanEvent(rawData, callbacks);

      if (result.isAnalysisReady && opts.handOffOnAnalysisReady) {
        if (doneRef.current || holdingRef.current) return;
        if (!opts.readyToHandOff) { finishRun(); return; }
        holdingRef.current = true;
        let waitingSince = Date.now();
        let lastProgress = opts.holdProgress?.() ?? 0;
        holdPollRef.current = window.setInterval(() => {
          if (cancelled) return;
          if (opts.readyToHandOff?.()) { finishRun(); return; }
          const progress = opts.holdProgress?.() ?? 0;
          if (progress !== lastProgress) {
            lastProgress = progress;
            waitingSince = Date.now();
          }
          if (Date.now() - waitingSince >= HANDOFF_HOLD_MAX_MS) finishRun();
        }, HOLD_POLL_MS);
        return;
      }

      if (result.isDone) {
        finishRun();
        return;
      }

      if (result.isFailure) {
        clearHold();
        clearInactivityTimer();
        es?.close();
        doneRef.current = true;
        triggerFail();
        return;
      }

      if (result.signalDelta > 0) {
        signalsRef.current += result.signalDelta;
        opts.onSignalsChange?.(signalsRef.current);
      }

      if (result.progressDelta > 0) bump(result.progressDelta);
    };

    const connectStream = (scanId: string) => {
      const params = new URLSearchParams();
      if (dbIdRef.current) params.set('dbId', String(dbIdRef.current));
      if (lastEventId) params.set('lastEventId', lastEventId);
      const query = params.toString();
      es = openOnboardingStream(`${SCAN_BASE}/${scanId}/stream${query ? `?${query}` : ''}`, { withCredentials: true });
      resetInactivityTimer();

      es.onmessage = (e) => {
        clearReconnectTimer();
        resetInactivityTimer();
        reopensLeft = MAX_STREAM_REOPENS;
        if (e.lastEventId) lastEventId = e.lastEventId;
        if (!cancelled) handleMessage(e.data as string);
      };

      es.onerror = () => {
        if (cancelled || doneRef.current) return;
        if (es?.readyState === EventSource.CLOSED) {
          reopenOrFail('lost');
          return;
        }
        if (!reconnectTimer) {
          reconnectTimer = setTimeout(() => {
            if (!cancelled && !doneRef.current) reopenOrFail('lost');
          }, RECONNECT_TIMEOUT_MS);
        }
      };
    };

    const run = async () => {
      if (opts.initialScanId) {
        scanIdRef.current = opts.initialScanId;
        if (!cancelled) connectStream(opts.initialScanId);
        return;
      }
      const createScan = async (): Promise<ScanCreateResult | null> => {
        const res = await fetch(SCAN_BASE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ companyName: opts.name, nameSource: opts.nameSource, websiteUrl: opts.url, businessType: opts.businessType }),
        });
        if (res.status === 409) {
          const body = await res.json().catch(() => ({})) as { companyName?: string; companyId?: number };
          if (!cancelled) {
            triggerFail('duplicate', { existingCompany: body?.companyName, existingCompanyId: body?.companyId });
          }
          return null;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          notifyAiLimit(body);
          throw new Error(`Scan failed: ${res.status}`);
        }
        return await res.json() as ScanCreateResult;
      };

      try {
        const data = await replayRest('scans:create', createScan);
        if (cancelled || !data) return;
        if (data.dbId) dbIdRef.current = data.dbId;
        scanIdRef.current = data.scanId;
        opts.onScanCreated?.(data.scanId);
        if (data.companyId) opts.onCompanyCreated?.(data.companyId);
        connectStream(data.scanId);
      } catch {
        if (!cancelled) triggerFail();
      }
    };

    void run();
    return () => { cancelled = true; clearReconnectTimer(); clearInactivityTimer(); clearHold(); es?.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.resumeKey]);

  return { scanIdRef, setProgressBounds };
}
